/**
 * Copyright 2023-2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useMemo } from "react";
import { Else, If, Then, When } from "react-if";
import styled from "styled-components";
import {
  Box,
  ExtendedTooltip,
  FlexContainer,
  Table,
  Text,
  theme,
} from "@nordcloud/gnui";
import { ExecutionStatus } from "~/generated/graphql";
import { StyledSVGIcon } from "~/components/Styled/StyledSVGIcon";
import { ROUTES } from "~/routing/routes";
import { isNotNil } from "~/tools";
import { isNotFinishedExecution } from "~/views/events/EventsDetails/Tabs/Details/components/utils";
import type {
  EventResourceGroup,
  EventResourceState,
} from "~/views/events/EventsDetails/Tabs/Details/hooks/useEventResourceController/types";
import { NameWithLink } from "../Tables/cells/NameWithLink";
import { EventResourceStateItem } from "./EventResourceStateItem";
import { getResourceStatusConfig, STATUS_CONFIGS } from "./utils";

type Props = {
  states: EventResourceState[];
  resourceGroup: EventResourceGroup["resourceGroup"];
  resourceGroupId?: EventResourceGroup["resourceGroupId"];
  resourceGroupStatus: EventResourceGroup["resourceGroupStatus"];
  eventActionEndTime?: string;
  eventId: string;
  eventActionId: string;
  actionStatus?: ExecutionStatus;
};

export function EventResourceGroupItem({
  states,
  resourceGroup,
  resourceGroupId,
  resourceGroupStatus,
  eventId,
  eventActionId,
  eventActionEndTime,
  actionStatus,
}: Props) {
  const groupStatusConfig = useMemo(() => {
    if (!isNotFinishedExecution(actionStatus)) {
      return getResourceStatusConfig(resourceGroupStatus);
    }
    return STATUS_CONFIGS["UNKNOWN"];
  }, [actionStatus, resourceGroupStatus]);

  return (
    <Box innerSpacing="spacing00">
      <When condition={resourceGroupId}>
        <StyledBox padding={theme.spacing.spacing03}>
          <FlexContainer css={{ width: "100%" }}>
            <ExtendedTooltip
              caption={
                <>
                  {groupStatusConfig.description}
                  <When condition={isNotNil(eventActionEndTime)}>
                    <div>{eventActionEndTime}</div>
                  </When>
                </>
              }
              status={groupStatusConfig.tooltipColor}
              position="start"
            >
              <StyledSVGIcon
                name={groupStatusConfig.icon}
                color={groupStatusConfig.color()}
                css={{ minWidth: theme.spacing.spacing04 }}
                pathColors={groupStatusConfig.pathColors}
              />
            </ExtendedTooltip>
            <Text tag="div" weight="medium" ml={theme.spacing.spacing03}>
              <If condition={isNotNil(resourceGroup)}>
                <Then>
                  <NameWithLink
                    name={resourceGroup?.name ?? ""}
                    link={`${ROUTES.resourceGroups.generateDetails(
                      resourceGroupId ?? ""
                    )}`}
                  />
                  <Text
                    css={{ wordBreak: "break-word" }}
                    tag="div"
                    size="sm"
                    color={theme.color.text.text02}
                  >
                    {resourceGroupId}
                  </Text>
                </Then>
                <Else>
                  <ExtendedTooltip
                    caption={"Resource group has been deleted"}
                    position="start"
                  >
                    <Text size="md" color={theme.color.text.text02}>
                      {resourceGroupId}
                    </Text>
                  </ExtendedTooltip>
                </Else>
              </If>
            </Text>
          </FlexContainer>
        </StyledBox>
      </When>
      <Table>
        <Table.tbody>
          {states?.map((state) => (
            <EventResourceStateItem
              key={state.id}
              state={state}
              eventId={eventId}
              eventActionId={eventActionId}
            />
          ))}
        </Table.tbody>
      </Table>
    </Box>
  );
}

const StyledBox = styled(Box)`
  border-bottom: 2px solid ${theme.color.border.border01};
  border-bottom-right-radius: 0;
  border-bottom-left-radius: 0;
`;
